import React from 'react';
import { Plus, Save, CheckCircle, RotateCcw, AlertTriangle, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AfternoonTransferTable } from './AfternoonTransferTable';
import { AfternoonTransferSession, UpdateAfternoonTransferRowPayload } from '../types/bread-transfers.types';

interface AfternoonTransferTabProps {
  session: AfternoonTransferSession | null;
  onCreateSession: () => Promise<AfternoonTransferSession>;
  onUpdateRow: (payload: UpdateAfternoonTransferRowPayload) => Promise<void>;
  onCompleteSession: () => Promise<void>;
  onResetSession: () => Promise<void>;
  loading?: boolean;
}

export const AfternoonTransferTab: React.FC<AfternoonTransferTabProps> = ({
  session,
  onCreateSession,
  onUpdateRow,
  onCompleteSession,
  onResetSession,
  loading = false
}) => {
  const [totalSuggested, setTotalSuggested] = React.useState(0);
  const handleCreateSession = async () => {
    try {
      await onCreateSession();
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleCompleteSession = async () => {
    if (!session) return;
    
    const hasIncompleteData = session.locations.some(location => 
      location.currentStock === 0 && location.suggested === 0
    );

    if (hasIncompleteData) {
      alert('Hay ubicaciones sin datos completos. Por favor revisa antes de completar.');
      return;
    }

    try {
      await onCompleteSession();
      alert('Transferencia de tarde completada exitosamente');
    } catch (error) {
      console.error('Error completing session:', error);
      alert('Error al completar la transferencia');
    }
  };

  const handleResetSession = async () => {
    if (!window.confirm('¿Estás seguro de que deseas reiniciar esta sesión? Se perderán todos los cambios.')) {
      return;
    }

    try {
      await onResetSession();
    } catch (error) {
      console.error('Error resetting session:', error);
    }
  };

  if (!session) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 animate-spin">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Cargando transferencia de tarde...
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              Los datos se están cargando automáticamente.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalSuggestedFromSession = totalSuggested || session.totals.suggested;
  const hasData = session.locations.some(location => location.currentStock > 0);

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Transferencia Tarde
          </h2>
          {/* Only show badge for completed status */}
          {session.status === 'completed' && (
            <Badge 
              variant="default"
              className="bg-green-600"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Completado
            </Badge>
          )}
        </div>
        
        <div className="flex gap-2">
          {session.status === 'completed' && (
            <Button
              variant="outline"
              onClick={handleResetSession}
              disabled={loading}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reiniciar
            </Button>
          )}
          
          {session.status === 'draft' && (
            <Button
              onClick={handleCompleteSession}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Completar Transferencia
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sugerido</CardTitle>
            <CheckCircle className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {totalSuggestedFromSession}
            </div>
            <p className="text-xs text-muted-foreground">
              unidades sugeridas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stock Actual Total</CardTitle>
            <Package className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {session.totals.currentStock}
            </div>
            <p className="text-xs text-muted-foreground">
              unidades disponibles
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Venta Hasta el Momento</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {session.totals.saleUntilNow}
            </div>
            <p className="text-xs text-muted-foreground">
              unidades vendidas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <AfternoonTransferTable
        locations={session.locations}
        totals={session.totals}
        onUpdateRow={onUpdateRow}
        onTotalSuggestedChange={setTotalSuggested}
        disabled={session.status === 'completed' || loading}
      />

      {/* Information */}
      {!hasData && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <span>Los datos se basan en información histórica simulada</span>
        </div>
      )}
    </div>
  );
};